import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import { useParams } from 'react-router-dom';
import type { TradeLogicTunableManifestV2 } from '@einhaus/project314-types';
import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout';
import UserLayout from '@/components/Layout/UserLayout';
import { Button, Callout, Card, DataTable, StatusBadge } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { useAppQuery } from '@/hooks/useAppQuery';
import CandidateFoldsModal from './components/CandidateFoldsModal';
import ConfirmationsCard from './components/ConfirmationsCard';
import PromoteCandidateModal from './components/PromoteCandidateModal';
import CreateResearchVariantModal from './components/CreateResearchVariantModal';
import ScanCurveChart from './components/ScanCurveChart';
import WorkbenchRecommendationsCard from './components/WorkbenchRecommendationsCard';
import {
    formatEntryOptimizerStatus,
    formatMetric,
    formatParamVector,
    formatPercent,
    formatTimestamp,
    getEntryOptimizerStatusTone,
    isEntryOptimizerJobActive,
    parseBlockingReasons,
    parseScanPoints,
    type EntryOptimizerCandidate,
    type EntryOptimizerJob
} from './entryOptimizerUtils';

const EntryOptimizerDetails = () => {
    const { jobId: jobIdParam } = useParams();
    const jobId = Number.parseInt(jobIdParam ?? '', 10);
    const queryClient = useQueryClient();

    const [foldsCandidate, setFoldsCandidate] = useState<EntryOptimizerCandidate | null>(null);
    const [researchCandidate, setResearchCandidate] = useState<EntryOptimizerCandidate | null>(null);
    const [promoteCandidate, setPromoteCandidate] = useState<EntryOptimizerCandidate | null>(null);

    const jobQuery = useAppQuery({
        ...trpc.entryOptimizer.job.queryOptions({ jobId }),
        enabled: Number.isInteger(jobId) && jobId > 0,
        refetchInterval: (query) => (query.state.data?.job && isEntryOptimizerJobActive(query.state.data.job) ? 5000 : false)
    });

    const job = jobQuery.data?.job ?? null;
    const isTerminal = job !== null && !isEntryOptimizerJobActive(job);

    const scansQuery = useAppQuery({
        ...trpc.entryOptimizer.parameterScans.queryOptions({ jobId }),
        enabled: job !== null && isTerminal
    });

    const candidatesQuery = useAppQuery({
        ...trpc.entryOptimizer.listCandidates.queryOptions({ jobId }),
        enabled: job !== null && isTerminal
    });

    const cancelJobMutation = useMutation({
        ...trpc.entryOptimizer.cancelJob.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: async () => {
            showToast('Tuning cancelled', 'success');
            await queryClient.invalidateQueries(trpc.entryOptimizer.job.pathFilter());
        }
    });

    const confirmationsQuery = useAppQuery({
        ...trpc.entryOptimizer.listConfirmations.queryOptions({ jobId }),
        enabled: job !== null && isTerminal,
        refetchInterval: (query) =>
            (query.state.data?.confirmations ?? []).some(
                (confirmation) => confirmation.status === 'pending' || confirmation.status === 'running'
            )
                ? 10000
                : false
    });

    const confirmations = confirmationsQuery.data?.confirmations ?? [];

    const requestConfirmationMutation = useMutation({
        ...trpc.entryOptimizer.requestConfirmation.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: async () => {
            showToast('Verification run started — this re-tests the setting with a real run', 'success');
            await queryClient.invalidateQueries(trpc.entryOptimizer.listConfirmations.pathFilter());
        }
    });

    const manifest = useMemo<TradeLogicTunableManifestV2 | null>(() => {
        if (!job) return null;

        try {
            return JSON.parse(job.tunableManifestJson) as TradeLogicTunableManifestV2;
        } catch {
            return null;
        }
    }, [job]);

    const candidates = candidatesQuery.data?.candidates ?? [];
    const scans = scansQuery.data?.scans ?? [];

    const columns: ColumnDef<EntryOptimizerCandidate>[] = [
        {
            accessorKey: 'rank',
            header: 'Rank',
            cell: ({ row }) => (
                <span className="text-zinc-300">
                    {row.original.rank ?? '—'}
                    {row.original.isParetoFinalist === 1 && <span className="ml-1 text-amber-300">★</span>}
                </span>
            )
        },
        {
            id: 'params',
            header: 'Settings',
            cell: ({ row }) => (
                <div>
                    <div className="font-medium text-zinc-100">{formatParamVector(row.original)}</div>
                    <div className="max-w-[320px] truncate text-xs text-zinc-500" title={row.original.summary}>
                        {row.original.isIncumbent === 1 ? 'Your current settings' : row.original.summary}
                    </div>
                </div>
            )
        },
        {
            accessorKey: 'entryCount',
            header: 'Entries',
            cell: ({ row }) => (
                <div>
                    <div className="text-zinc-300">{row.original.entryCount.toLocaleString()}</div>
                    <div className="text-xs text-zinc-500">{row.original.uniqueEntities ?? '—'} entities</div>
                </div>
            )
        },
        {
            accessorKey: 'avgProfit',
            header: 'Avg profit',
            cell: ({ row }) => <MetricCell value={row.original.avgProfit} />
        },
        {
            accessorKey: 'winRate',
            header: 'Win rate',
            cell: ({ row }) => <span className="text-zinc-300">{formatPercent(row.original.winRate)}</span>
        },
        {
            accessorKey: 'liftVsIncumbent',
            header: 'vs current',
            cell: ({ row }) => <MetricCell signed value={row.original.liftVsIncumbent} />
        },
        {
            accessorKey: 'holdoutLift',
            header: 'On unseen data',
            cell: ({ row }) => <MetricCell signed value={row.original.holdoutLift} />
        },
        {
            accessorKey: 'cvSignAgreement',
            header: 'Consistency',
            cell: ({ row }) => <span className="text-zinc-300">{formatMetric(row.original.cvSignAgreement, 1)}</span>
        },
        {
            id: 'gates',
            header: 'Validation',
            cell: ({ row }) => <GatesBadge candidate={row.original} />
        },
        {
            id: 'actions',
            header: '',
            cell: ({ row }) => (
                <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline-dark" onClick={() => setFoldsCandidate(row.original)}>
                        Details
                    </Button>
                    <Button
                        disabled={job?.status !== 'completed' || row.original.isIncumbent === 1 || requestConfirmationMutation.isPending}
                        size="sm"
                        variant="outline-dark"
                        onClick={() => requestConfirmationMutation.mutate({ candidateId: row.original.id, jobId })}
                    >
                        Verify
                    </Button>
                    <Button
                        disabled={job?.status !== 'completed' || row.original.isIncumbent === 1}
                        size="sm"
                        onClick={() => setResearchCandidate(row.original)}
                    >
                        Create variant
                    </Button>
                </div>
            )
        }
    ];

    if (!Number.isInteger(jobId) || jobId <= 0) {
        return (
            <UserLayout containerSize="1400px" title="Entry Tuning - Project 314">
                <div className="rounded-md border border-red-900/60 bg-red-950/40 p-3 text-sm text-red-100">Invalid run id.</div>
            </UserLayout>
        );
    }

    return (
        <UserLayout containerSize="1400px" title="Entry Tuning Results - Project 314">
            <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-zinc-100">Tuning Results</h1>
                    {job && (
                        <p className="mt-1 text-sm text-zinc-400">
                            Strategy #{job.strategyId} · {job.duration} timeframe · held {Math.round(job.horizonSeconds / 86400)} day
                            {Math.round(job.horizonSeconds / 86400) === 1 ? '' : 's'} · {formatTimestamp(job.createdAt)} · run #
                            {job.envelopeExperimentId}
                        </p>
                    )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {job && (
                        <StatusBadge status={job.status} tone={getEntryOptimizerStatusTone(job.status)}>
                            {formatEntryOptimizerStatus(job.status)}
                        </StatusBadge>
                    )}
                    {job && isEntryOptimizerJobActive(job) && (
                        <Button
                            disabled={cancelJobMutation.isPending}
                            size="sm"
                            variant="destructive"
                            onClick={() => cancelJobMutation.mutate({ jobId })}
                        >
                            Cancel
                        </Button>
                    )}
                    <Button size="sm" to="/user/entryOptimizer" variant="outline-dark">
                        Back to Entry Tuning
                    </Button>
                </div>
            </div>

            {jobQuery.isLoading ? (
                <DataLoadingSpinner text="Loading results..." />
            ) : jobQuery.error instanceof Error ? (
                <div className="rounded-md border border-red-900/60 bg-red-950/40 p-3 text-sm text-red-100">{jobQuery.error.message}</div>
            ) : job ? (
                <>
                    {isTerminal && job.status === 'completed' && (
                        <Callout className="mb-4" variant={(job.candidatesAfterCV ?? 0) > 0 ? 'success' : 'info'}>
                            {(job.candidatesAfterCV ?? 0) > 0
                                ? `We found ${job.candidatesAfterCV} setting${job.candidatesAfterCV === 1 ? '' : 's'} that beat your current entry and passed validation. Review them below and create an inactive family variant after confirming its real-entry behavior.`
                                : 'No setting passed the economic checks. You can still confirm a candidate and create an inactive research variant for comparison; the results remain exploratory.'}
                        </Callout>
                    )}

                    <JobStats job={job} />

                    {job.errorMessage && (
                        <div className="mt-4 rounded-md border border-red-900/60 bg-red-950/40 p-3 text-sm text-red-100">
                            {job.errorMessage}
                        </div>
                    )}

                    {isTerminal && <WorkbenchRecommendationsCard jobId={jobId} />}

                    {isTerminal && <ConfirmationsCard confirmations={confirmations} />}

                    {isTerminal && (
                        <Card
                            className="mt-4"
                            subtitle="Best first. Create an inactive family variant for backtesting after real-entry confirmation. Economic checks inform the comparison; they do not block research creation."
                            title="Suggested settings"
                        >
                            {candidatesQuery.isLoading ? (
                                <DataLoadingSpinner text="Loading suggestions..." />
                            ) : candidates.length === 0 ? (
                                <div className="text-sm text-zinc-500">No results recorded.</div>
                            ) : (
                                <DataTable sort columns={columns} data={candidates} pagination={{ enabled: true, pageSize: 25 }} />
                            )}
                        </Card>
                    )}

                    {isTerminal && scans.length > 0 && (
                        <Card className="mt-4" title="How each setting scored">
                            <details>
                                <summary className="cursor-pointer text-sm text-zinc-300">
                                    Show the per-setting performance curves (advanced)
                                </summary>
                                <div className="mt-4 flex flex-col gap-6">
                                    {scans.map((scan) => (
                                        <ScanCurveChart
                                            bestValue={scan.bestValue}
                                            key={scan.id}
                                            label={scan.label}
                                            manifest={manifest}
                                            points={parseScanPoints(scan)}
                                            tunableId={scan.tunableId}
                                        />
                                    ))}
                                </div>
                            </details>
                        </Card>
                    )}
                </>
            ) : null}

            <CandidateFoldsModal candidate={foldsCandidate} jobId={jobId} onClose={() => setFoldsCandidate(null)} />

            {researchCandidate?.jobId === jobId && (
                <CreateResearchVariantModal
                    key={`${jobId}:${researchCandidate.id}`}
                    jobId={jobId}
                    candidateId={researchCandidate.id}
                    summary={`${formatParamVector(researchCandidate)} — ${researchCandidate.summary}`}
                    onClose={() => setResearchCandidate(null)}
                    onAdvanced={() => {
                        setPromoteCandidate(researchCandidate);
                        setResearchCandidate(null);
                    }}
                />
            )}

            <PromoteCandidateModal
                key={promoteCandidate?.id ?? 'closed'}
                candidate={promoteCandidate}
                confirmations={confirmations}
                jobId={jobId}
                manifest={manifest}
                onClose={() => setPromoteCandidate(null)}
            />
        </UserLayout>
    );
};

const JobStats = ({ job }: { job: EntryOptimizerJob }) => (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Entries analyzed" value={job.totalEntries?.toLocaleString() ?? '—'} />
        <StatCard label="Settings tested" value={job.combinationsEvaluated?.toLocaleString() ?? '—'} />
        <StatCard label="Beat your entry" tone="success" value={String(job.candidatesAfterCV ?? '—')} />
        <StatCard label="Time taken" value={job.runtimeSeconds === null ? '—' : `${job.runtimeSeconds}s`} />
    </div>
);

const StatCard = ({ label, tone = 'default', value }: { label: string; tone?: 'default' | 'success'; value: string }) => (
    <Card bodyClassName="p-4">
        <div className="text-xs uppercase tracking-wide text-zinc-500">{label}</div>
        <div className={`mt-1 text-2xl font-semibold ${tone === 'success' ? 'text-green-300' : 'text-zinc-100'}`}>{value}</div>
    </Card>
);

const MetricCell = ({ signed = false, value }: { signed?: boolean; value: number | string | null }) => {
    const numeric = value === null ? null : Number(value);

    const className =
        signed && numeric !== null && !Number.isNaN(numeric)
            ? numeric > 0
                ? 'text-green-300'
                : numeric < 0
                  ? 'text-red-300'
                  : 'text-zinc-300'
            : 'text-zinc-300';

    return <span className={className}>{formatMetric(value)}</span>;
};

const GatesBadge = ({ candidate }: { candidate: EntryOptimizerCandidate }) => {
    if (candidate.passesGates === 1) {
        return (
            <StatusBadge status="passed" tone="success">
                Validated
            </StatusBadge>
        );
    }

    const reasons = parseBlockingReasons(candidate);

    return (
        <span title={reasons.join('\n')}>
            <StatusBadge status="blocked" tone="warning">
                Didn&apos;t pass ({reasons.length})
            </StatusBadge>
        </span>
    );
};

export default EntryOptimizerDetails;
